import className from 'classnames'
import { Fragment, useMemo } from 'react'
import { Link } from 'features/i18n/router'
import { useTranslation } from 'react-i18next'

import useChatRoomLogic from '../../hocs/useChatRoomLogic'
import { MODAL_MODE } from 'components/Modal/config'
import { fromNowToFullDate } from 'lib/time'
import { ROUTE_PATH } from 'lib/paths'
import { useLinger } from 'hooks'
import { ChatRoom, User } from 'lib/_types'
import { useCurrentUser } from 'store/api/yay/user'
import {
  chatRoomToChatRoomContextSchema,
  logEvent,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import usePageModal from 'features/Modal/hooks/usePageModal'

import AvatarGroup from 'components/Avatar/AvatarGroup'
import Avatar from 'components/Avatar'
import Nickname from 'components/Nickname'
import LoadMore from 'components/LoadMore'
import AsyncListDataComponent from 'components/AsyncListDataComponent'
import Menu from 'components/Menu'
import PinChatIcon from 'components/Icons/PinChatIcon'

import './style.scss'

const ChatRoomList = ({
  isLoading,
  chatRooms = [],
  isFullyLoaded,
  onLoadMore,
  pinnable,
  hideable = true,
  actionButton,
  noLink,
  emptyPlaceholder,
  loadMoreRef,
  reloadOnDelete,
}: {
  isLoading?: boolean
  chatRooms?: ChatRoom[]
  isFullyLoaded: boolean
  onLoadMore?: () => void
  pinnable?: boolean
  hideable?: boolean
  actionButton?: any
  noLink?: boolean
  emptyPlaceholder?: string
  loadMoreRef?: React.MutableRefObject<any>
  reloadOnDelete?: boolean
}) => {
  const { t } = useTranslation()

  const { data: currentUser } = useCurrentUser()

  return (
    <AsyncListDataComponent
      isLoading={isLoading}
      isLoaded={!isLoading}
      data={chatRooms}
      placeholder={
        <span
          dangerouslySetInnerHTML={{
            __html: emptyPlaceholder ?? t('chat_room_list.empty_placeholder'),
          }}
        />
      }
    >
      {(chatRooms: ChatRoom[]) => {
        return (
          <dl className="ChatRoomList">
            {chatRooms.map((chatRoom) => {
              const member = (chatRoom?.members ?? [])?.find?.(
                (member: any) => member.id !== currentUser?.id
              )

              return (
                <ChatRoomListItem
                  key={chatRoom.id}
                  chatRoom={chatRoom}
                  member={member}
                  pinnable={pinnable}
                  actionButton={actionButton}
                  noLink={noLink}
                  hideable={hideable}
                  reloadOnDelete={reloadOnDelete}
                />
              )
            })}

            <LoadMore
              isLoading={isLoading}
              className="ChatRoomList__load-more"
              onClick={onLoadMore}
              hidden={isFullyLoaded || !onLoadMore}
              ref={loadMoreRef}
            />
          </dl>
        )
      }}
    </AsyncListDataComponent>
  )
}

export default ChatRoomList

const ChatRoomListItem = ({
  chatRoom,
  member,
  pinnable,
  hideable = true,
  actionButton,
  noLink,
  reloadOnDelete,
}: {
  chatRoom: ChatRoom
  member: User
  pinnable?: boolean
  hideable?: boolean
  actionButton?: any
  noLink?: boolean
  reloadOnDelete?: boolean
}) => {
  const { t } = useTranslation()
  const { generatePageModalUrl } = usePageModal()

  const { data: currentUser } = useCurrentUser()

  const { onPinChatRoom, onDeleteChat, onUnPinChatRoom, onHideChatRoom } =
    useChatRoomLogic({ chatRoomId: chatRoom?.id })

  const menu = useMemo(() => {
    const _menu = [
      {
        title: t('common.delete'),
        onClick: () => {
          onDeleteChat({
            onSuccess: () => {
              snowplowEvent.delete_chat({
                user: userToUserContextSchemaData({ user: member }),
                chatRoom: chatRoomToChatRoomContextSchema({ chatRoom }),
              })

              if (reloadOnDelete) window.location.reload()
            },
          })
        },
      },
    ]

    if (hideable) {
      _menu.push({
        title: t('common.hide'),
        onClick: onHideChatRoom,
      })
    }

    if (pinnable) {
      if (chatRoom?._pinned) {
        _menu.push({
          title: t('common.unpin'),
          onClick: onUnPinChatRoom,
        })
      } else {
        _menu.push({
          title: t('common.pin'),
          onClick: onPinChatRoom,
        })
      }
    }

    return _menu
  }, [chatRoom?._pinned])

  const { inViewRef } = useLinger({
    enterCallback: (params) => {
      if (params.first_impression) {
        snowplowEvent.impression({
          chatRoom: chatRoomToChatRoomContextSchema({
            chatRoom,
          }),
        })
      }
    },
  })

  return (
    <div
      onClick={(e) => {
        if (typeof actionButton === 'function') {
          e.preventDefault()
        } else {
          logEvent.chat_room_open({
            from: 'chat_list',
            target_chat_room_id: chatRoom.id.toString(),
          })
        }
      }}
      className={className('ChatRoomList__item', {
        'ChatRoomList__item--chatroom': true,
        'ChatRoomList__item--banned': (chatRoom?.members?.length ?? 0) < 2,
      })}
      ref={inViewRef}
    >
      {noLink ? (
        <div className="ChatRoomList__item__handle" draggable={false} />
      ) : (
        <Link
          className="ChatRoomList__item__handle"
          draggable={false}
          to={generatePageModalUrl(MODAL_MODE.CHAT_ROOM, {
            chatRoomId: chatRoom.id,
          })}
        />
      )}

      <div className="ChatRoomList__item__thumbnail">
        {chatRoom.is_group ? (
          <AvatarGroup users={chatRoom.members} />
        ) : (
          <Link
            to={ROUTE_PATH.USER(member?.id)}
            onClick={() =>
              logEvent.user_profile_open({
                target_user_id: member?.id?.toString(),
                from: 'undefined',
              })
            }
          >
            <Avatar user={member} />
          </Link>
        )}

        {chatRoom?._pinned && <PinChatIcon />}
      </div>

      <div className="ChatRoomList__item__info">
        <h3 className="ChatRoomList__item__h3">
          {chatRoom?.is_group ? (
            chatRoom?.name ? (
              `(${chatRoom?.members?.length}) ${chatRoom?.name}`
            ) : (
              `(${chatRoom?.members.length}) ${(chatRoom?.members ?? [])
                .map((member: any) => member.nickname)
                .join(', ')}`
            )
          ) : (
            <Nickname user={member} />
          )}
        </h3>

        {(chatRoom?.last_message?.message_type === 'sticker' ||
          chatRoom?.last_message?.message_type === 'gif' ||
          chatRoom?.last_message?.message_type === 'image' ||
          chatRoom?.last_message?.message_type === 'eternal_image' ||
          chatRoom?.last_message?.message_type === 'video' ||
          chatRoom?.last_message?.message_type === 'eternal_video') && (
          <Fragment>
            {(() => {
              let _messageType = chatRoom?.last_message?.message_type

              if (_messageType == 'eternal_image') {
                _messageType = 'image'
              } else if (_messageType == 'eternal_video') {
                _messageType = 'video'
              }

              const messageType = t(`common.message_type.${_messageType}`)

              const sender =
                chatRoom?.last_message?.user_id === currentUser?.id
                  ? t('chat_room_list_item.message.media.self', {
                      messageType,
                    })
                  : t('chat_room_list_item.message.media.other', {
                      messageType,
                    })

              return <p className="ChatRoomList__item__p">{sender}</p>
            })()}
          </Fragment>
        )}

        {chatRoom?.last_message?.message_type === 'screenshot_warning' && (
          <p className="ChatRoomList__item__p">
            {chatRoom?.last_message?.user_id === currentUser?.id
              ? t('common.self_screen_shot_warning')
              : t('common.screen_shot_warning')}
          </p>
        )}

        {chatRoom?.last_message?.message_type === 'individual_call' && (
          <p className="ChatRoomList__item__p">
            {chatRoom?.last_message?.user_id === currentUser?.id
              ? t('chat_room_list_item.message.individual_call.self')
              : t('chat_room_list_item.message.individual_call.other')}
          </p>
        )}

        {chatRoom?.last_message?.message_type === 'text' &&
          chatRoom?.last_message?.text && (
            <p className="ChatRoomList__item__p">
              {chatRoom?.last_message?.text}
            </p>
          )}

        {chatRoom?.last_message?.message_type === 'deleted' && (
          <p className="ChatRoomList__item__p">
            {chatRoom?.last_message?.user_id === currentUser?.id
              ? t('chat_room_list_item.message.deleted.self')
              : t('chat_room_list_item.message.deleted.other')}
          </p>
        )}
      </div>

      <div className="ChatRoomList__item__detail">
        {typeof actionButton === 'function' ? (
          actionButton(chatRoom)
        ) : (
          <>
            <time className="ChatRoomList__item__detail__time">
              {fromNowToFullDate(
                (chatRoom?.last_message?.created_at ?? chatRoom?.updated_at) *
                  1000
              )}
            </time>

            <div className="ChatRoomList__item__detail__mics">
              {(chatRoom?.unread_count ?? 0) > 0 && (
                <span className="ChatRoomList__item__detail__mics__badge">
                  {chatRoom?.unread_count}
                </span>
              )}

              <Menu
                arrowPosition="right"
                icon="3dots-v"
                onToggle={(_: any, e: any) => e.preventDefault()}
                options={menu}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
